import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Text from 'UIKit/Text'
import HunnyTooltip from 'UIKit/Tooltip'
import { LayoutConfig } from 'config/layout'
import { GameDetail } from 'config/types/game'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import React, { useRef } from 'react'
import { Trans } from 'react-i18next'
import { useAppStatus, useMenuContainer, useToggleSidebar } from 'state/app/hooks'
import { useAuth } from 'state/auth/hooks'
import { useUnwatchedNewBonusListener } from 'state/bonus/hooks'
import styled, { css } from 'styled-components'
import { Icons } from 'svgs'
import theme from 'theme'
import GameDesktopIframePopup from 'views/GameDetails/GameDesktopIframePopup'
import AdditionalContent from './Components/AdditionalContent'
import BottomBar from './Components/BottomBar'
import Column from './Components/Column'
import Footer from './Components/Footer'
import { Menu, MenuContainer } from './Components/Menu'
import Notification from './Components/Notification/NotificationLayout'
import NotificationBar from './Components/NotificationBar'
import { Sidebar } from './Components/SideBar'
import Topbar from './Components/Topbar'

const StyledMenuBlank = styled(MenuContainer)`
  display: none;

  ${({ theme }) => theme.mediaQueries.xl} {
    display: flex;
  }
`

const StyledLayout = styled(Column)<{ $menuOpen: boolean; $isHideMenu: boolean }>`
  justify-content: space-between;
  transition: ${({ theme: { transitions } }) => transitions.fast};
  width: 100%;

  ${({ theme: { mediaQueries } }) => mediaQueries.xl} {
    width: ${({ $menuOpen, $isHideMenu, theme: { navigationWidth, navigationSmallWidth } }) => {
      const $menuWidth = $isHideMenu ? 0 : $menuOpen ? navigationWidth : navigationSmallWidth

      return `calc(100% - ${$menuWidth}px)`
    }};
  }
`

const StyledContentLayout = styled(Flex)`
  width: 100%;
  flex-direction: column;
  justify-content: space-between;
`

const StyledContainer = styled(Flex)`
  width: 100%;
  min-height: calc(var(--screen-height) - ${({ theme }) => theme.topbarHeight});
  padding-bottom: ${({ theme }) => theme.bottombarHeight};

  ${({ theme }) => theme.mediaQueries.md} {
    padding-bottom: 0;
  }
`

const StyledFooter = styled(Box)`
  width: 100%;
  background: ${(props) => props.theme.colors.background};
`

const StyledContentContainer = styled(Box)`
  transition: 0.2s;
  opacity: 1;
`

const StyledContent = styled(Box)`
  margin: 0px auto;
`

const StyledLayoutContainer = styled(Flex)`
  width: 100%;
  max-width: 100vw;
`

const StyledBackground = styled(Box)`
  position: fixed;
  pointer-events: none;
  height: var(--screen-height);
  width: 100%;
  top: 0;
  left: 0;
  background-color: transparent;
  background-image: radial-gradient(at 82% 100%, hsla(244, 87%, 33%, 0.5) 10%, transparent 40%),
    radial-gradient(at 10% 50%, hsl(273.92deg 79.21% 27.32% / 52%) 7%, transparent 70%);
`

const StyledNotificationContainer = styled(Flex)<{ $smallMenu: boolean }>`
  background: ${({ theme }) => theme.colors.textTertiary} !important;
  border-radius: ${({ theme }) => theme.radii.tiny};
  gap: 8px;
  padding: 4px 8px;
  z-index: 6;
  position: absolute;
  top: 90px;
  left: 230px;
  transition: 0.2s all;
  line-height: 20px;
  align-items: center;

  ${({ $smallMenu }) =>
    $smallMenu &&
    css`
      left: 65px;
    `};

  &:before {
    content: '';
    position: absolute;
    top: 9.5px;
    left: -4px;
    width: 10px;
    height: 10px;
    transform: rotate(135deg);
    border-radius: 1px;
    background: ${({ theme }) => theme.colors.textTertiary} !important;
    backdrop-filter: blur(6px);
    z-index: -2;
  }

  display: none;

  ${({ theme }) => theme.mediaQueries.md} {
    display: flex;
  }
`

const StyledCloseIcon = styled(Flex)`
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    color: #6c727e;
    fill: #6c727e;
  }
`

const NotificationBadge = () => {
  const { isMenuOpen } = useMenuContainer()
  const [unwatchedNewBonus, onClose] = useUnwatchedNewBonusListener()
  const { isSigned } = useAuth()

  return (
    unwatchedNewBonus &&
    isSigned && (
      <StyledNotificationContainer $smallMenu={!isMenuOpen}>
        <Text color="#161C24" fontSize="12px" fontWeight={500}>
          <Trans>You have received a new reward, check it out now!</Trans>
        </Text>
        <StyledCloseIcon
          width="12px"
          height="12px"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onClose()
          }}
        >
          <Icons.ClearIcon width="12px" height="12px" />
        </StyledCloseIcon>
      </StyledNotificationContainer>
    )
  )
}

const MainLayout: React.FC<
  BoxProps & {
    enabledMeshBackground?: boolean
    htmlContent?: string
    layoutConfig?: LayoutConfig
  }
> = ({ children, maxWidth, htmlContent, layoutConfig, ...props }) => {
  const { isMenuOpen } = useMenuContainer()

  return (
    <StyledLayoutContainer id="root-layout" minHeight="var(--screen-height)">
      <StyledBackground
        display={[
          layoutConfig?.meshBackground?.mobile?.visible ? 'block' : 'none',
          ,
          layoutConfig?.meshBackground?.tablet?.visible ? 'block' : 'none',
          ,
          layoutConfig?.meshBackground?.desktop?.visible ? 'block' : 'none',
        ]}
      />

      <Box position="fixed" width="100vw" maxWidth="100vw" height="0px" zIndex={theme.zIndices.dropdown}>
        <NotificationBar />
        <Flex width="100%" height="0px" position="relative">
          <NotificationBadge />

          <Menu layoutConfig={layoutConfig?.menu} />

          <Topbar
            display={[
              !layoutConfig?.header?.mobile?.hidden ? 'block' : 'none',
              ,
              !layoutConfig?.header?.tablet?.hidden ? 'block' : 'none',
              ,
              !layoutConfig?.header?.desktop?.hidden ? 'block' : 'none',
            ]}
            logoDisplayConfig={{
              tablet: {
                hidden: layoutConfig?.menu?.tablet ? !layoutConfig.menu.tablet.hidden : true,
              },
              desktop: {
                hidden: layoutConfig?.menu?.desktop ? !layoutConfig.menu.desktop.hidden : true,
              },
            }}
          />
          <Notification />
        </Flex>
      </Box>

      <StyledContainer>
        {!layoutConfig?.menu?.desktop?.hidden && (
          <Box display={['none', '', '', '', '', 'flex']}>
            <StyledMenuBlank zIndex="-1 !important" />
          </Box>
        )}

        <StyledLayout
          pt={[
            !layoutConfig?.header?.mobile?.hidden
              ? 'calc(65px + var(--notification-bar-height))'
              : 'var(--notification-bar-height)',
            ,
            !layoutConfig?.header?.tablet?.hidden
              ? 'calc(65px + var(--notification-bar-height))'
              : 'var(--notification-bar-height)',
            ,
            !layoutConfig?.header?.desktop?.hidden
              ? 'calc(65px + var(--notification-bar-height))'
              : 'var(--notification-bar-height)',
          ]}
          pl={[
            '0px',
            '',
            '',
            layoutConfig?.menu?.tablet?.hidden ? '0px' : '65px',
            layoutConfig?.menu?.desktop?.hidden ? '0px' : '65px',
            '0px',
          ]}
          $menuOpen={isMenuOpen}
          $isHideMenu={layoutConfig?.menu?.desktop?.hidden}
          overflowX="unset"
        >
          <StyledContentLayout overflowX="unset">
            <DesktopGameScreen {...props} />
            <Content maxWidth={maxWidth} {...props}>
              {children}
              {htmlContent && <AdditionalContent htmlContent={htmlContent} />}
            </Content>
          </StyledContentLayout>

          <StyledFooter
            display={[
              !layoutConfig?.footer?.mobile?.hidden ? 'block' : 'none',
              ,
              !layoutConfig?.footer?.tablet?.hidden ? 'block' : 'none',
              ,
              !layoutConfig?.footer?.desktop?.hidden ? 'block' : 'none',
            ]}
          >
            <StyledContent>
              <Footer />
            </StyledContent>
          </StyledFooter>
        </StyledLayout>
      </StyledContainer>

      <Box
        position="fixed"
        width="100%"
        height="0px"
        zIndex={[11, 11, 5]}
        display={[
          layoutConfig?.sideBar?.mobile?.hidden ? 'none' : 'block',
          ,
          layoutConfig?.sideBar?.tablet?.hidden ? 'none' : 'block',
          ,
          layoutConfig?.sideBar?.desktop?.hidden ? 'none' : 'block',
        ]}
      >
        <SidebarContainer />
      </Box>

      <BottomBar
        display={[
          layoutConfig?.bottomBar?.mobile?.hidden ? 'none !important' : 'fixed',
          ,
          layoutConfig?.bottomBar?.tablet?.hidden ? 'none !important' : 'fixed',
          '',
        ]}
      />
    </StyledLayoutContainer>
  )
}

const SidebarContainer = () => {
  const { isSidebarOpen, closeSidebar } = useToggleSidebar()
  const backdropRef = useRef()
  const containerRef = useRef(null)

  const handleClickBackdop = (e) => {
    if (containerRef.current && !containerRef.current.contains(e.target)) closeSidebar()
  }
  return (
    <StyledSideBarBackdrop ref={backdropRef} $isOpen={isSidebarOpen} onClick={(e) => handleClickBackdop(e)}>
      <StyledSidebar ref={containerRef}>
        <Sidebar />
      </StyledSidebar>
    </StyledSideBarBackdrop>
  )
}

const StyledSidebar = styled(Box)``

const DesktopGameScreen = ({ gameDetail, ...props }: { gameDetail?: GameDetail }) => {
  const { isMobile } = useMatchBreakpoints()
  return !isMobile && <GameDesktopIframePopup gameDetail={gameDetail} {...props} />
}

const Content: React.FC<BoxProps> = ({ children, maxWidth, ...props }) => {
  const { isFinishFetch } = useAppStatus()

  return (
    <StyledContent minHeight="var(--screen-height)" maxWidth={maxWidth} {...props}>
      <StyledContentContainer>{isFinishFetch && children}</StyledContentContainer>

      <HunnyTooltip id="common-tip-left" place="left" />
      <HunnyTooltip id="common-tip-right" place="right" />
      <HunnyTooltip id="common-tip-bottom" place="bottom" />
      <HunnyTooltip id="common-tip-top" place="top" />
    </StyledContent>
  )
}

MainLayout.defaultProps = {
  width: '100%',
  maxWidth: `${theme.siteWidth}px`,
}

const StyledSideBarBackdrop = styled(Box)<{ $isOpen: boolean }>`
  width: 100%;
  z-index: 4;
  top: 0;
  height: ${({ $isOpen }) => ($isOpen ? 'var(--screen-height)' : '0px')};

  will-change: transition;

  background: none;
  transition: ${({ theme: { transitions } }) => transitions.medium};
  left: ${({ $isOpen }) => ($isOpen ? '0%' : '100%')};

  ${({ theme: { mediaQueries } }) => mediaQueries.md} {
    position: absolute;
    top: ${({ theme: { topbarHeight } }) => topbarHeight};

    height: ${({ theme: { topbarHeight } }) => `calc(100vh - ${topbarHeight})`};
  }
`
export default MainLayout
